import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUpdateMyRole, UserRole, getGetMeQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

export function RoleSwitcher() {
  // Demo affordance only: previewing other roles is a development convenience.
  // In production builds it is hidden, and the server gates PATCH /me/role too.
  // Real role assignment happens on the admin "Users & Roles" screen.
  if (!import.meta.env.DEV) return null;
  return <RoleSwitcherControl />;
}

function RoleSwitcherControl() {
  const { mutate: updateRole, isPending } = useUpdateMyRole();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleRoleChange = (role: string) => {
    updateRole(
      { data: { role: role as UserRole } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
          toast({
            title: "Role Updated",
            description: `Previewing as ${role.replace('_', ' ')}`,
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update role",
            variant: "destructive",
          });
        }
      }
    );
  };

  return (
    <Select onValueChange={handleRoleChange} disabled={isPending}>
      <SelectTrigger className="h-8 w-[140px] text-xs bg-gray-50 border-gray-200 text-gray-600 focus:ring-0">
        <SelectValue placeholder="Preview Role..." />
      </SelectTrigger>
      <SelectContent>
        {Object.values(UserRole).map((r) => (
          <SelectItem key={r} value={r} className="text-xs">
            {r.replace('_', ' ')}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
